/**
 * GraphQL Service for User Management
 *
 * Handles all GraphQL API calls for user operations using AWS Amplify
 */

import { generateClient } from "aws-amplify/api";
import awsconfig from "aws-exports";
import { getValidToken } from "utils/tokenUtils";
import { parseTasksPayload } from "../../tasksParse";

/**
 * Save User Mutation
 *
 * GraphQL mutation to save/create a user
 */
const SAVE_USER_MUTATION = `
  mutation SaveUser($profileId: String, $input: UserInput) {
    saveUser(profileId: $profileId, input: $input) {
      EventId
      Message
    }
  }
`;

/**
 * Admin Save User Mutation
 *
 * GraphQL mutation to save/create a user with entity association (for admin)
 */
const ADMIN_SAVE_USER_MUTATION = `
  mutation AdminSaveUser($entityId: String, $entityType: String!, $profileId: String, $input: UserInput) {
    adminsaveUser(entityId: $entityId, entityType: $entityType, profileId: $profileId, input: $input) {
      EventId
      Message
    }
  }
`;

/**
 * Update User Role Mutation
 *
 * Switches the current user's active profile/context (role + entity).
 * After calling, use getUserProfile to get the updated profile and menu.
 */
const UPDATE_USER_ROLE_MUTATION = `
  mutation UpdateUserRole($input: updateUserInput!) {
    updateUserRole(input: $input) {
      EventId
      Message
    }
  }
`;

/**
 * Update the current user's active role/entity (switch profile context).
 *
 * @param {Object} input - updateUserInput: profileId, email, name, role?, entityId?, entityType?
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<Object>} Response with EventId and Message
 */
export const updateUserRole = async (input, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!input?.profileId || !input?.email || !input?.name) {
    throw new Error("profileId, email, and name are required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: UPDATE_USER_ROLE_MUTATION,
        variables: {
          input: {
            profileId: input.profileId,
            email: input.email,
            name: input.name,
            ...(input.role != null && { role: input.role }),
            ...(input.entityId != null && { entityId: input.entityId }),
            ...(input.entityType != null && { entityType: input.entityType }),
          },
        },
      }),
    });

    const result = await response.json();

    if (result.errors?.length > 0) {
      const errorMessages = result.errors.map((e) => e.message).join(", ");
      throw new Error(errorMessages || "Failed to update user role");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = result.data?.updateUserRole;
    return {
      success: true,
      data,
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("An unexpected error occurred while updating user role");
  }
};

/**
 * Save user via GraphQL API using Amplify
 *
 * @param {Object} userData - User data object
 * @param {string} userData.name - User's full name
 * @param {string} userData.email - User's email
 * @param {string} userData.phone - User's phone number (optional)
 * @param {string} userData.mobile - User's mobile number (optional)
 * @param {string} userData.timezone - User's timezone
 * @param {string} userData.locale - User's locale
 * @param {string} userData.role - User's role
 * @param {string} userData.employeeId - Employee ID (optional)
 * @param {string} userData.preference - User preferences (optional)
 * @param {string} userData.clientType - Client type (optional)
 * @param {Object} userData.location - Location object
 * @param {string} userData.location.city - City
 * @param {string} userData.location.state - State
 * @param {string} userData.location.locationId - Location ID (optional)
 * @param {string} idToken - Cognito ID token for authentication (required)
 * @param {string} profileId - Profile ID (optional, defaults to empty string)
 * @returns {Promise<Object>} Response with EventId and Message
 */
export const saveUser = async (userData, idToken, profileId = "") => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }

  try {
    // Ensure token is valid and refresh if necessary
    const validToken = await getValidToken(idToken);
    // Helper function to check if a value is non-empty
    // AWSJSON and other fields may have validation issues with empty strings
    const isNonEmpty = (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    };

    // Prepare the input object according to API schema (UserInput: association, email, employeeId, locale, location, mobile, name, phone)
    // Only include fields with non-empty values to avoid AWSJSON validation issues
    const input = {};

    // Required/Common fields - only include if non-empty
    if (isNonEmpty(userData.name)) {
      input.name = userData.name.trim();
    }
    if (isNonEmpty(userData.email)) {
      input.email = userData.email.trim();
    }
    if (isNonEmpty(userData.phone)) {
      input.phone = userData.phone.trim();
    }
    if (isNonEmpty(userData.mobile)) {
      input.mobile = userData.mobile.trim();
    }
    if (isNonEmpty(userData.locale)) {
      input.locale = userData.locale.trim();
    }
    if (isNonEmpty(userData.employeeId)) {
      input.employeeId = userData.employeeId.trim();
    }
    if (isNonEmpty(userData.status)) {
      input.status = userData.status.trim();
    }

    // Location object - only include if at least one field is non-empty
    const location = {};
    if (isNonEmpty(userData.location?.city)) {
      location.city = userData.location.city.trim();
    }
    if (isNonEmpty(userData.location?.state)) {
      location.state = userData.location.state.trim();
    }
    if (isNonEmpty(userData.location?.locationId)) {
      location.locationId = userData.location.locationId.trim();
    }

    // Only add location to input if it has at least one field
    if (Object.keys(location).length > 0) {
      input.location = location;
    }

    // association: [userAssociation] - multiple roles per entity
    if (
      userData.associations &&
      Array.isArray(userData.associations) &&
      userData.associations.length > 0
    ) {
      input.association = userData.associations
        .filter((a) => a && isNonEmpty(a.entityType) && isNonEmpty(a.role))
        .map((a) => {
          const item = {
            entityType: a.entityType.trim(),
            role: a.role.trim(),
          };
          if (isNonEmpty(a.entityId)) item.entityId = a.entityId.trim();
          return item;
        });
      if (input.association.length === 0) delete input.association;
    }

    // Prepare variables - only include profileId if it's non-empty
    const variables = {
      input: input,
    };

    // Only include profileId if it's non-empty
    if (profileId && profileId.trim() !== "") {
      variables.profileId = profileId.trim();
    }

    // Use fetch directly with AppSync endpoint and custom auth token
    // This gives us full control over the Authorization header
    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken, // AppSync with Cognito User Pools expects the ID token directly (no Bearer prefix)
      },
      body: JSON.stringify({
        query: SAVE_USER_MUTATION,
        variables: variables,
      }),
    });

    const result = await response.json();

    // Handle GraphQL errors
    if (result.errors && result.errors.length > 0) {
      const errorMessages = result.errors.map((err) => err.message).join(", ");
      throw new Error(
        errorMessages || "Failed to save user. Please try again.",
      );
    }

    // Handle HTTP errors
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
      data: result.data?.saveUser,
      eventId: result.data?.saveUser?.EventId,
      message: result.data?.saveUser?.Message,
    };
  } catch (error) {
    // Handle network errors
    if (error.message) {
      throw new Error(error.message);
    }

    throw new Error("An unexpected error occurred while saving the user");
  }
};

/**
 * Admin save user via GraphQL API (with entity association)
 *
 * @param {Object} userData - User data object (same as saveUser)
 * @param {string} idToken - Cognito ID token for authentication (required)
 * @param {string} entityId - Entity ID (airport or nonprofit ID) (optional)
 * @param {string} entityType - Entity type ("Airport" or "NonProfit") (required if entityId is provided)
 * @param {string} profileId - Profile ID (optional, defaults to empty string)
 * @returns {Promise<Object>} Response with EventId and Message
 */
export const adminSaveUser = async (
  userData,
  idToken,
  entityId = null,
  entityType = null,
  profileId = "",
) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }

  if (entityId && !entityType) {
    throw new Error("Entity type is required when entity ID is provided");
  }

  try {
    // Ensure token is valid and refresh if necessary
    const validToken = await getValidToken(idToken);
    // Helper function to check if a value is non-empty
    const isNonEmpty = (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    };

    // Prepare the input object according to API schema
    const input = {};

    // Required/Common fields - only include if non-empty
    if (isNonEmpty(userData.name)) {
      input.name = userData.name.trim();
    }
    if (isNonEmpty(userData.email)) {
      input.email = userData.email.trim();
    }
    if (isNonEmpty(userData.phone)) {
      input.phone = userData.phone.trim();
    }
    if (isNonEmpty(userData.mobile)) {
      input.mobile = userData.mobile.trim();
    }
    if (isNonEmpty(userData.timezone)) {
      input.timezone = userData.timezone.trim();
    }
    if (isNonEmpty(userData.locale)) {
      input.locale = userData.locale.trim();
    }
    if (isNonEmpty(userData.role)) {
      input.role = userData.role.trim();
    }
    if (isNonEmpty(userData.employeeId)) {
      input.employeeId = userData.employeeId.trim();
    }
    if (isNonEmpty(userData.preference)) {
      input.preference = userData.preference.trim();
    }
    if (isNonEmpty(userData.status)) {
      input.status = userData.status.trim();
    }

    // Location object
    const location = {};
    if (isNonEmpty(userData.location?.city)) {
      location.city = userData.location.city.trim();
    }
    if (isNonEmpty(userData.location?.state)) {
      location.state = userData.location.state.trim();
    }
    if (isNonEmpty(userData.location?.locationId)) {
      location.locationId = userData.location.locationId.trim();
    }

    if (Object.keys(location).length > 0) {
      input.location = location;
    }

    // Prepare variables
    const variables = {
      input: input,
      entityType: entityType || "user", // Default to "user" if not provided
    };

    if (entityId && entityId.trim() !== "") {
      variables.entityId = entityId.trim();
    }

    if (profileId && profileId.trim() !== "") {
      variables.profileId = profileId.trim();
    }

    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: ADMIN_SAVE_USER_MUTATION,
        variables: variables,
      }),
    });

    const result = await response.json();

    // Handle GraphQL errors
    if (result.errors && result.errors.length > 0) {
      const errorMessages = result.errors.map((err) => err.message).join(", ");
      throw new Error(
        errorMessages || "Failed to save user. Please try again.",
      );
    }

    // Handle HTTP errors
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
      data: result.data?.adminsaveUser,
      eventId: result.data?.adminsaveUser?.EventId,
      message: result.data?.adminsaveUser?.Message,
    };
  } catch (error) {
    if (error.message) {
      throw new Error(error.message);
    }
    throw new Error("An unexpected error occurred while saving the user");
  }
};

/**
 * Get Users Query
 *
 * Backend pagination: item_count + nextToken (start_position / limit are no-ops).
 */
const GET_USERS_QUERY = `
  query GetUsers($item_count: Int, $nextToken: String) {
    getUsers(item_count: $item_count, nextToken: $nextToken)
  }
`;

const USERS_API_PAGE_SIZE = 20;

function parseUsersPayload(data) {
  if (data == null) return { list: [], nextToken: null };

  let parsed = data;
  while (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return { list: [], nextToken: null };
    }
  }

  if (Array.isArray(parsed)) {
    return { list: parsed, nextToken: null };
  }

  if (!parsed || typeof parsed !== "object") {
    return { list: [], nextToken: null };
  }

  const list =
    (Array.isArray(parsed.ProfileList) && parsed.ProfileList) ||
    (Array.isArray(parsed.UsersList) && parsed.UsersList) ||
    (Array.isArray(parsed.items) && parsed.items) ||
    [];

  const nextToken =
    parsed.nextToken ?? parsed.NextToken ?? parsed.next_token ?? null;

  return {
    list,
    nextToken:
      nextToken != null && String(nextToken).trim() !== ""
        ? String(nextToken)
        : null,
  };
}

/**
 * Get users via GraphQL API (one page).
 *
 * @param {string} idToken - Cognito ID token (required)
 * @param {number|null} itemCount - Page size (default 20)
 * @param {string|null} nextToken - Cursor from previous response for Next
 * @returns {Promise<{ success: boolean, data: Array, nextToken: string|null, hasMore: boolean }>}
 */
export const getUsers = async (
  idToken,
  itemCount = USERS_API_PAGE_SIZE,
  nextToken = null,
) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;
    const variables = {};

    if (
      itemCount !== null &&
      itemCount !== undefined &&
      typeof itemCount === "number"
    ) {
      variables.item_count = itemCount;
    }
    if (nextToken != null && String(nextToken).trim() !== "") {
      variables.nextToken = String(nextToken).trim();
    }

    const requestBody = {
      query: GET_USERS_QUERY,
    };
    if (Object.keys(variables).length > 0) {
      requestBody.variables = variables;
    }

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();

    if (result.errors && result.errors.length > 0) {
      const errorMessages = result.errors.map((err) => err.message).join(", ");
      throw new Error(errorMessages || "Failed to fetch users");
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = result.data?.getUsers;
    const { list, nextToken: paginationToken } = parseUsersPayload(data);

    return {
      success: true,
      data: list,
      nextToken: paginationToken,
      hasMore: Boolean(paginationToken),
    };
  } catch (error) {
    if (error.message) {
      throw new Error(error.message);
    }
    throw new Error("An unexpected error occurred while fetching users");
  }
};

export { USERS_API_PAGE_SIZE };

/**
 * Get User Query (by profileId)
 *
 * GraphQL query to fetch a single user's details
 */
const GET_USER_QUERY = `
  query GetUser($profileId: String) {
    getUser(profileId: $profileId)
  }
`;

/**
 * Get a single user by profileId
 *
 * @param {string} profileId - User profile ID
 * @param {string} idToken - Cognito ID token for authentication (required)
 * @returns {Promise<Object>} Response with user data
 */
export const getUser = async (profileId, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!profileId || !String(profileId).trim()) {
    throw new Error("Profile ID is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: GET_USER_QUERY,
        variables: { profileId: String(profileId).trim() },
      }),
    });

    const result = await response.json();

    if (result.errors && result.errors.length > 0) {
      const errorMessages = result.errors.map((err) => err.message).join(", ");
      throw new Error(errorMessages || "Failed to fetch user");
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = result.data?.getUser;
    const parsedData = typeof data === "string" ? JSON.parse(data) : data;

    return {
      success: true,
      data: parsedData,
    };
  } catch (error) {
    if (error.message) {
      throw new Error(error.message);
    }
    throw new Error("An unexpected error occurred while fetching user");
  }
};

/**
 * Get User Profile Query
 *
 * GraphQL query to fetch the current authenticated user's profile
 */
const GET_USER_PROFILE_QUERY = `
  query GetUserProfile {
    getUserProfile {
      profileId
      name
      email
      phone
      mobile
      role
      status
      locale
      timezone
      employeeId
      entityId
      entityType
      ip
      lastLogin
      created
      updated
      deleted
      loggedProfileId
      loggedUserId
      performance
      preference
      feature {
        name
        description
        isEnabled
        isReadOnly
        isSystemLevel
        isVisible
        CreatedAt
        UpdatedAt
        menu {
          heading
          description
          icon
          url
          access
          order
          isVisible
          CreatedAt
          UpdatedAt
        }
      }
    }
  }
`;

/**
 * Get user profile via GraphQL API
 *
 * @param {string} idToken - Cognito ID token for authentication (required)
 * @returns {Promise<Object>} Response with user profile data
 */
export const getUserProfile = async (idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }

  try {
    // Ensure token is valid and refresh if necessary
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: GET_USER_PROFILE_QUERY,
      }),
    });

    const result = await response.json();

    if (result.errors && result.errors.length > 0) {
      const errorMessages = result.errors.map((err) => err.message).join(", ");
      throw new Error(errorMessages || "Failed to fetch user profile");
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // getUserProfile returns Profile type directly (not AWSJSON)
    const profileData = result.data?.getUserProfile;

    return {
      success: true,
      data: profileData,
    };
  } catch (error) {
    if (error.message) {
      throw new Error(error.message);
    }
    throw new Error("An unexpected error occurred while fetching user profile");
  }
};

const GET_TASKS_QUERY = `
  query GetTasks($limit: String, $nextToken: String) {
    getTasks(limit: $limit, nextToken: $nextToken)
  }
`;

const GET_ENTITIES_QUERY = `
  query GetEntities($limit: String, $nextToken: String) {
    getEntities(limit: $limit, nextToken: $nextToken)
  }
`;

const GET_ENTITY_BY_ID_QUERY = `
  query GetEntityById($entityId: String!) {
    getEntitybyId(entityId: $entityId)
  }
`;

/**
 * Get Actions Query
 *
 * GraphQL query to fetch available actions for a resource type (Mission, ResourceRequest, etc.)
 */
const GET_ACTIONS_QUERY = `
  query GetActions($resourceType: String!) {
    getActions(resourceType: $resourceType)
  }
`;

const GET_TIMELINE_QUERY = `
  query GetTimeline($missionId: String!) {
    getTimeline(missionId: $missionId)
  }
`;

const GET_MISSION_DOCUMENTS_QUERY = `
  query GetMissionDocuments($missionId: String!) {
    getMissionDocuments(missionId: $missionId)
  }
`;

const GET_RESOURCE_REQUEST_DOCUMENTS_QUERY = `
  query GetResourceRequestDocuments($requestId: String!) {
    getResourceRequestDocuments(requestId: $requestId)
  }
`;

const GET_PILOT_DOCUMENTS_QUERY = `
  query GetPilotDocuments($pilotId: String!) {
    getPilotDocuments(pilotId: $pilotId)
  }
`;

const GET_FILE_MUTATION = `
  mutation GetFile($fileName: String!, $bucket: String) {
    getFile(fileName: $fileName, bucket: $bucket) {
      Message
      StatusCode
    }
  }
`;

const INITIALIZE_FILE_MUTATION = `
  mutation InitializeFile($fileName: String!) {
    initialize(fileName: $fileName) {
      Message
      StatusCode
    }
  }
`;

const GET_PRESIGNED_URL_MUTATION = `
  mutation GetPresignedUrl($fileId: String, $fileKey: String, $parts: Int) {
    getPresignedUrl(fileId: $fileId, fileKey: $fileKey, parts: $parts) {
      Message
      StatusCode
    }
  }
`;

const FINALIZE_FILE_MUTATION = `
  mutation FinalizeFile($input: finalizeInput) {
    finalize(input: $input)
  }
`;

async function executeGraphqlRequest(query, variables, idToken) {
  const validToken = await getValidToken(idToken);
  const endpoint = awsconfig.API.GraphQL.endpoint;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: validToken,
    },
    body: JSON.stringify({ query, variables }),
  });
  const result = await response.json();
  if (result.errors?.length > 0) {
    const msg = result.errors.map((e) => e.message).join(", ");
    throw new Error(msg || "GraphQL request failed");
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return result;
}

/**
 * Get a presigned download URL for a file in S3.
 *
 * @param {string} fileName - S3 object key / path
 * @param {string} [bucket] - Bucket name (backend may default if omitted)
 * @param {string} idToken - Cognito ID token
 * @returns {Promise<{ Message: string, StatusCode: string }>}
 */
export const getFile = async (fileName, bucket, idToken) => {
  if (!idToken) throw new Error("Authentication token is required");
  if (!fileName) throw new Error("fileName is required");

  const variables = { fileName: String(fileName).trim() };
  if (bucket != null && String(bucket).trim() !== "") {
    variables.bucket = String(bucket).trim();
  }

  const result = await executeGraphqlRequest(
    GET_FILE_MUTATION,
    variables,
    idToken,
  );
  return result.data?.getFile;
};

/**
 * Start multipart upload (S3 CreateMultipartUpload equivalent).
 */
export const initializeFileUpload = async (fileName, idToken) => {
  if (!idToken) throw new Error("Authentication token is required");
  if (!fileName) throw new Error("fileName is required");
  const result = await executeGraphqlRequest(
    INITIALIZE_FILE_MUTATION,
    { fileName: String(fileName).trim() },
    idToken,
  );
  return result.data?.initialize;
};

/**
 * Get presigned URLs for each upload part.
 */
export const getPresignedUploadUrls = async (
  fileId,
  fileKey,
  parts,
  idToken,
) => {
  if (!idToken) throw new Error("Authentication token is required");
  if (!fileId || !fileKey) {
    throw new Error("fileId and fileKey are required");
  }
  const partCount = parseInt(String(parts), 10);
  if (!partCount || partCount < 1) {
    throw new Error("parts must be a positive integer");
  }
  const result = await executeGraphqlRequest(
    GET_PRESIGNED_URL_MUTATION,
    {
      fileId: String(fileId).trim(),
      fileKey: String(fileKey).trim(),
      parts: partCount,
    },
    idToken,
  );
  return result.data?.getPresignedUrl;
};

/**
 * Complete multipart upload after all parts are uploaded.
 */
export const finalizeFileUpload = async (input, idToken) => {
  if (!idToken) throw new Error("Authentication token is required");
  if (!input?.fileId || !input?.fileKey) {
    throw new Error("fileId and fileKey are required for finalize");
  }
  const result = await executeGraphqlRequest(
    FINALIZE_FILE_MUTATION,
    { input },
    idToken,
  );
  const data = result.data?.finalize;
  if (data == null) return null;
  return typeof data === "string" ? JSON.parse(data) : data;
};

/**
 * Get available actions for a resource type
 *
 * @param {string} resourceType - "Mission" or "ResourceRequest"
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<Array<{actionId, type, subType, message, resourceType, role, entityType, ...}>>} List of action configs
 */
export const getActions = async (resourceType, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!resourceType) {
    throw new Error("resourceType is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: GET_ACTIONS_QUERY,
        variables: { resourceType },
      }),
    });

    const result = await response.json();

    if (result.errors?.length > 0) {
      const errorMessages = result.errors.map((e) => e.message).join(", ");
      throw new Error(errorMessages || "Failed to fetch actions");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = result.data?.getActions;
    let parsed = typeof data === "string" ? JSON.parse(data) : data;

    if (!parsed) return [];
    const list = Array.isArray(parsed)
      ? parsed
      : parsed.actions ??
        parsed.ActionsList ??
        parsed.items ??
        parsed.list ??
        [];
    const items = Array.isArray(list) ? list : [];

    return items
      .filter((a) => {
        const types = a.resourceType ?? [];
        return (
          Array.isArray(types) &&
          types.some((t) => t.toLowerCase() === resourceType.toLowerCase())
        );
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((a) =>
        typeof a === "string"
          ? { type: a, message: a }
          : {
              ...a,
              label: a.message ?? a.label ?? a.type ?? a.name ?? String(a),
            },
      );
  } catch (error) {
    if (error.message) {
      throw new Error(error.message);
    }
    throw new Error("An unexpected error occurred while fetching actions");
  }
};

/**
 * Get mission timeline via GraphQL API
 *
 * @param {string} missionId - Mission ID (required)
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<{ success: boolean, data: * }>} Timeline payload (AWSJSON)
 */
export const getTimeline = async (missionId, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!missionId) {
    throw new Error("missionId is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: GET_TIMELINE_QUERY,
        variables: { missionId: String(missionId).trim() },
      }),
    });

    const result = await response.json();

    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to fetch timeline");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = result.data?.getTimeline;
    const parsed =
      data == null ? null : typeof data === "string" ? JSON.parse(data) : data;

    return { success: true, data: parsed };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("Failed to fetch timeline");
  }
};

/**
 * Fetch documents linked to a mission.
 *
 * @param {string} missionId - Mission ID (required)
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<{ success: boolean, data: * }>} Documents payload (AWSJSON)
 */
export const getMissionDocuments = async (missionId, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!missionId) {
    throw new Error("missionId is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: GET_MISSION_DOCUMENTS_QUERY,
        variables: { missionId: String(missionId).trim() },
      }),
    });

    const result = await response.json();

    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to fetch mission documents");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    let parsed = result.data?.getMissionDocuments;
    while (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        break;
      }
    }

    return {
      success: true,
      data: parsed ?? null,
      raw: result.data?.getMissionDocuments,
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("Failed to fetch mission documents");
  }
};

/**
 * Fetch documents linked to a resource request.
 *
 * @param {string} requestId - Resource request ID (required)
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<{ success: boolean, data: * }>}
 */
export const getResourceRequestDocuments = async (requestId, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!requestId) {
    throw new Error("requestId is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: GET_RESOURCE_REQUEST_DOCUMENTS_QUERY,
        variables: { requestId: String(requestId).trim() },
      }),
    });

    const result = await response.json();

    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to fetch resource request documents");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    let parsed = result.data?.getResourceRequestDocuments;
    while (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        break;
      }
    }

    return {
      success: true,
      data: parsed ?? null,
      raw: result.data?.getResourceRequestDocuments,
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("Failed to fetch resource request documents");
  }
};

/**
 * Fetch documents linked to a pilot profile.
 *
 * @param {string} pilotId - Pilot profile ID (required)
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<{ success: boolean, data: * }>}
 */
export const getPilotDocuments = async (pilotId, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!pilotId) {
    throw new Error("pilotId is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: GET_PILOT_DOCUMENTS_QUERY,
        variables: { pilotId: String(pilotId).trim() },
      }),
    });

    const result = await response.json();

    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to fetch pilot documents");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = result.data?.getPilotDocuments;
    const parsed =
      data == null ? null : typeof data === "string" ? JSON.parse(data) : data;

    return { success: true, data: parsed };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("Failed to fetch pilot documents");
  }
};

/**
 * Add an action to a mission
 *
 * @param {string} missionId - Mission ID (required)
 * @param {Object} input - actionInput: { message, type, subType?, data? }
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<{EventId, Message}>}
 */
export const addMissionAction = async (missionId, input, idToken) => {
  if (!idToken) throw new Error("Authentication token is required");
  if (!missionId) throw new Error("missionId is required");
  if (!input?.message || !input?.type)
    throw new Error("message and type are required");

  const validToken = await getValidToken(idToken);
  const endpoint = awsconfig.API.GraphQL.endpoint;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: validToken,
    },
    body: JSON.stringify({
      query: `
        mutation AddMissionAction($missionId: String!, $input: actionInput) {
          addMissionAction(missionId: $missionId, input: $input) {
            EventId
            Message
          }
        }
      `,
      variables: { missionId, input },
    }),
  });

  const result = await response.json();
  if (result.errors?.length > 0) {
    throw new Error(result.errors.map((e) => e.message).join(", "));
  }
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  return result.data?.addMissionAction ?? {};
};

/**
 * Update a task (accept/reject pilot interest, etc.).
 *
 * @param {string} taskId - Task ID (required)
 * @param {Object} input - actionInput: { message, type, subType?, data? }
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<{EventId, Message}>}
 */
export const updateTask = async (taskId, input, idToken) => {
  if (!idToken) throw new Error("Authentication token is required");
  if (!taskId) throw new Error("taskId is required");
  if (!input?.message || !input?.type)
    throw new Error("message and type are required");

  const validToken = await getValidToken(idToken);
  const endpoint = awsconfig.API.GraphQL.endpoint;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: validToken,
    },
    body: JSON.stringify({
      query: `
        mutation UpdateTask($taskId: String!, $input: actionInput) {
          updateTask(taskId: $taskId, input: $input) {
            EventId
            Message
          }
        }
      `,
      variables: { taskId: String(taskId).trim(), input },
    }),
  });

  const result = await response.json();
  if (result.errors?.length > 0) {
    throw new Error(result.errors.map((e) => e.message).join(", "));
  }
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  return result.data?.updateTask ?? {};
};

/**
 * Add an action to a resource request
 *
 * @param {string} requestId - Resource request ID (required)
 * @param {Object} input - actionInput: { message, type, subType?, data? }
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<{EventId, Message}>}
 */
export const addResourceRequestAction = async (requestId, input, idToken) => {
  if (!idToken) throw new Error("Authentication token is required");
  if (!requestId) throw new Error("requestId is required");
  if (!input?.message || !input?.type)
    throw new Error("message and type are required");

  const validToken = await getValidToken(idToken);
  const endpoint = awsconfig.API.GraphQL.endpoint;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: validToken,
    },
    body: JSON.stringify({
      query: `
        mutation AddResourceRequestAction($requestId: String!, $input: actionInput) {
          addResourceRequestAction(requestId: $requestId, input: $input) {
            EventId
            Message
          }
        }
      `,
      variables: { requestId, input },
    }),
  });

  const result = await response.json();
  if (result.errors?.length > 0) {
    throw new Error(result.errors.map((e) => e.message).join(", "));
  }
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  return result.data?.addResourceRequestAction ?? {};
};

const SAVE_ENTITY_MUTATION = `
  mutation SaveEntity($entityId: String, $input: entityInput) {
    saveEntity(entityId: $entityId, input: $input) {
      EventId
      Message
      entityId
    }
  }
`;

/**
 * Save Mission Mutation
 *
 * GraphQL mutation to create/update a mission
 */
const SAVE_MISSION_MUTATION = `
  mutation SaveMission($missionId: String, $input: missionInput) {
    saveMission(missionId: $missionId, input: $input) {
      EventId
      Message
      missionId
    }
  }
`;

/**
 * Save Resource Request Mutation
 *
 * GraphQL mutation to create/update a resource request
 */
const SAVE_RESOURCE_REQUEST_MUTATION = `
  mutation SaveResourceRequest($requestId: String, $input: resourceRequestInput) {
    saveResourceRequest(requestId: $requestId, input: $input) {
      EventId
      Message
      requestId
    }
  }
`;

function isNonEmptyValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string" && value.trim() === "") return false;
  return true;
}

function normalizeEntityTypeKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[-_\s]/g, "");
}

function isAirportEntityType(type) {
  return normalizeEntityTypeKey(type) === "airport";
}

function isNonprofitEntityType(type) {
  const key = normalizeEntityTypeKey(type);
  return key === "nonprofit" || key === "nonprofits";
}

function isAviPairEntityType(type) {
  return normalizeEntityTypeKey(type) === "avipair";
}

/** Backend entity type values: Airport | NonProfit | AviPair */
function canonicalEntityType(type) {
  if (isAirportEntityType(type)) return "Airport";
  if (isNonprofitEntityType(type)) return "NonProfit";
  if (isAviPairEntityType(type)) return "AviPair";
  return isNonEmptyValue(type) ? String(type).trim() : "";
}

function unwrapJsonValue(data) {
  let parsed = data;
  while (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return parsed;
    }
  }
  return parsed;
}

function normalizeEntityRecord(entity) {
  if (!entity || typeof entity !== "object" || Array.isArray(entity)) {
    return entity;
  }
  const id =
    entity.entityId ??
    entity.airportId ??
    entity.nonprofitId ??
    entity.nonProfitId ??
    entity.id;
  const type = String(entity.type || entity.entityType || "").trim();
  const airport =
    isAirportEntityType(type) ||
    (!type &&
      (isNonEmptyValue(entity.airportUse) ||
        isNonEmptyValue(entity.airportOwnership)));
  const nonprofit =
    isNonprofitEntityType(type) ||
    (!type && isNonEmptyValue(entity.ein) && !airport);
  const resolvedType =
    canonicalEntityType(type) ||
    (airport ? "Airport" : nonprofit ? "NonProfit" : "");
  return {
    ...entity,
    entityId: id,
    id,
    airportId: entity.airportId ?? (airport ? id : entity.airportId),
    nonprofitId:
      entity.nonprofitId ??
      entity.nonProfitId ??
      (nonprofit ? id : entity.nonprofitId),
    type: resolvedType,
  };
}

function collectEntityList(parsed) {
  if (Array.isArray(parsed)) return parsed;
  if (!parsed || typeof parsed !== "object") return [];

  const keys = [
    "EntitiesList",
    "entities",
    "items",
    "AirportsList",
    "NonProfitsList",
    "data",
    "list",
  ];
  const seen = new Set();
  const out = [];
  for (const key of keys) {
    if (!Array.isArray(parsed[key])) continue;
    for (const item of parsed[key]) {
      const rec = item && typeof item === "object" ? item : { value: item };
      const id =
        rec.entityId ?? rec.airportId ?? rec.nonprofitId ?? rec.nonProfitId ?? rec.id;
      const stamp = id != null ? String(id) : JSON.stringify(rec);
      if (seen.has(stamp)) continue;
      seen.add(stamp);
      out.push(rec);
    }
  }
  return out;
}

function parseEntitiesPayload(data) {
  const parsed = unwrapJsonValue(data);
  if (parsed == null) return { list: [], nextToken: null };

  const list = collectEntityList(parsed).map(normalizeEntityRecord);
  const nextToken =
    parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed.nextToken ?? parsed.NextToken ?? parsed.next_token ?? null
      : null;

  return {
    list,
    nextToken:
      nextToken != null && String(nextToken).trim() !== ""
        ? String(nextToken)
        : null,
  };
}

function unwrapEntityRecord(data) {
  let parsed = unwrapJsonValue(data);
  if (Array.isArray(parsed)) parsed = parsed[0];
  if (!parsed || typeof parsed !== "object") return null;

  const nested =
    parsed.entity ||
    parsed.Entity ||
    parsed.Item ||
    parsed.item ||
    (Array.isArray(parsed.EntitiesList) && parsed.EntitiesList[0]) ||
    (Array.isArray(parsed.AirportsList) && parsed.AirportsList[0]) ||
    (Array.isArray(parsed.NonProfitsList) && parsed.NonProfitsList[0]);

  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    parsed = nested;
  }

  return normalizeEntityRecord(parsed);
}

function buildEntityAddressInput(entityData) {
  if (Array.isArray(entityData.address)) {
    return entityData.address
      .filter(
        (addr) =>
          addr &&
          isNonEmptyValue(addr.address) &&
          isNonEmptyValue(addr.city) &&
          isNonEmptyValue(addr.state)
      )
      .map((addr) => ({
        address: String(addr.address).trim(),
        city: String(addr.city).trim(),
        state: String(addr.state).trim(),
      }));
  }

  if (
    typeof entityData.address === "string" &&
    isNonEmptyValue(entityData.address) &&
    isNonEmptyValue(entityData.city) &&
    isNonEmptyValue(entityData.state)
  ) {
    return [
      {
        address: entityData.address.trim(),
        city: entityData.city.trim(),
        state: entityData.state.trim(),
      },
    ];
  }

  if (
    entityData.address &&
    typeof entityData.address === "object" &&
    isNonEmptyValue(entityData.address.address) &&
    isNonEmptyValue(entityData.address.city) &&
    isNonEmptyValue(entityData.address.state)
  ) {
    return [
      {
        address: String(entityData.address.address).trim(),
        city: String(entityData.address.city).trim(),
        state: String(entityData.address.state).trim(),
      },
    ];
  }

  return [];
}

/**
 * Get entities via GraphQL API (unified airports + nonprofits).
 */
export const getEntities = async (idToken, limit = null, nextToken = null) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;
    const variables = {};
    if (limit) variables.limit = String(limit);
    if (nextToken != null && String(nextToken).trim() !== "") {
      variables.nextToken = String(nextToken).trim();
    }

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: GET_ENTITIES_QUERY,
        variables: Object.keys(variables).length > 0 ? variables : undefined,
      }),
    });

    const result = await response.json();

    if (result.errors && result.errors.length > 0) {
      const errorMessages = result.errors.map((err) => err.message).join(", ");
      throw new Error(errorMessages || "Failed to fetch entities");
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const { list, nextToken: pageToken } = parseEntitiesPayload(
      result.data?.getEntities
    );

    return {
      success: true,
      data: list,
      nextToken: pageToken,
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("An unexpected error occurred while fetching entities");
  }
};

async function getAllEntities(idToken) {
  const all = [];
  let token = null;
  let pages = 0;
  do {
    const page = await getEntities(idToken, null, token);
    all.push(...(Array.isArray(page.data) ? page.data : []));
    token = page.nextToken;
    pages += 1;
  } while (token && pages < 50);
  return all;
}

/**
 * Get a single entity by ID via GraphQL API.
 */
export const getEntityById = async (entityId, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!entityId) {
    throw new Error("Entity ID is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: GET_ENTITY_BY_ID_QUERY,
        variables: { entityId: String(entityId) },
      }),
    });

    const result = await response.json();

    if (result.errors && result.errors.length > 0) {
      const errorMessages = result.errors.map((err) => err.message).join(", ");
      throw new Error(errorMessages || "Failed to fetch entity");
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
      data: unwrapEntityRecord(result.data?.getEntitybyId),
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("An unexpected error occurred while fetching the entity");
  }
};

/**
 * Save (create/update) an entity via GraphQL API.
 */
export const saveEntity = async (entityData, idToken, entityId = null) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }

  try {
    const validToken = await getValidToken(idToken);

    if (!isNonEmptyValue(entityData?.name)) {
      throw new Error("Name is required");
    }

    const canonicalType = canonicalEntityType(entityData.type);
    const isAirport = isAirportEntityType(entityData.type);
    const category =
      (isNonEmptyValue(entityData.category) && String(entityData.category).trim()) ||
      (isAirport &&
        isNonEmptyValue(entityData.airportUse) &&
        String(entityData.airportUse).trim()) ||
      (isAirport ? "Airport" : "");
    const ein =
      (isNonEmptyValue(entityData.ein) && String(entityData.ein).trim()) ||
      (isAirport ? "N/A" : "");

    if (!category) {
      throw new Error("Category is required");
    }
    if (!ein) {
      throw new Error("EIN is required");
    }

    const input = {
      name: String(entityData.name).trim(),
      ein,
      category,
    };

    if (canonicalType) {
      input.type = canonicalType;
    }
    if (isNonEmptyValue(entityData.phone)) input.phone = String(entityData.phone).trim();
    if (isNonEmptyValue(entityData.city)) input.city = String(entityData.city).trim();
    if (isNonEmptyValue(entityData.state)) input.state = String(entityData.state).trim();
    if (isNonEmptyValue(entityData.email)) input.email = String(entityData.email).trim();
    if (isNonEmptyValue(entityData.url)) input.url = String(entityData.url).trim();
    if (isNonEmptyValue(entityData.description)) {
      input.description = String(entityData.description).trim();
    }
    if (isNonEmptyValue(entityData.logo)) input.logo = String(entityData.logo).trim();
    if (isNonEmptyValue(entityData.status)) {
      input.status = String(entityData.status).trim();
    }
    if (isNonEmptyValue(entityData.airportUse)) {
      input.airportUse = String(entityData.airportUse).trim();
    }
    if (isNonEmptyValue(entityData.airportOwnership)) {
      input.airportOwnership = String(entityData.airportOwnership).trim();
    }
    if (isNonEmptyValue(entityData.managerName)) {
      input.managerName = String(entityData.managerName).trim();
    }
    if (isNonEmptyValue(entityData.managerPhone)) {
      input.managerPhone = String(entityData.managerPhone).trim();
    }

    const addresses = buildEntityAddressInput(entityData);
    if (addresses.length > 0) {
      input.address = addresses;
      if (!input.city && addresses[0].city) input.city = addresses[0].city;
      if (!input.state && addresses[0].state) input.state = addresses[0].state;
    }

    if (Array.isArray(entityData.operationalStates)) {
      input.operationalStates = entityData.operationalStates
        .filter((s) => isNonEmptyValue(s))
        .map((s) => (typeof s === "string" ? s.trim() : String(s)));
    }

    const variables = { input };
    if (entityId && String(entityId).trim() !== "") {
      variables.entityId = String(entityId).trim();
    }

    const endpoint = awsconfig.API.GraphQL.endpoint;
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: SAVE_ENTITY_MUTATION,
        variables,
      }),
    });

    const result = await response.json();

    if (result.errors && result.errors.length > 0) {
      const errorMessages = result.errors.map((err) => err.message).join(", ");
      throw new Error(errorMessages || "Failed to save entity");
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const saved = result.data?.saveEntity;
    return {
      success: true,
      data: saved,
      eventId: saved?.EventId,
      message: saved?.Message,
      entityId: saved?.entityId,
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("An unexpected error occurred while saving the entity");
  }
};

/**
 * Get non-profits via GraphQL API (filters getEntities by type).
 */
export const getNonProfits = async (
  idToken,
  limit = null,
  nextToken = null,
) => {
  if (limit != null || nextToken != null) {
    const page = await getEntities(idToken, limit, nextToken);
    const data = (page.data || []).filter((e) => isNonprofitEntityType(e.type));
    return { success: true, data };
  }
  const all = await getAllEntities(idToken);
  return {
    success: true,
    data: all.filter((e) => isNonprofitEntityType(e.type)),
  };
};

/**
 * Get airports via GraphQL API (filters getEntities by type).
 */
export const getAirports = async (idToken) => {
  const all = await getAllEntities(idToken);
  return {
    success: true,
    data: all.filter((e) => isAirportEntityType(e.type)),
  };
};

/**
 * Get airport by ID via GraphQL API.
 */
export const getAirportById = async (airportId, idToken) => {
  return getEntityById(airportId, idToken);
};

/**
 * Get non-profit by ID via GraphQL API.
 */
export const getNonProfitById = async (nonprofitId, idToken) => {
  return getEntityById(nonprofitId, idToken);
};

/**
 * Save airport via unified saveEntity mutation.
 */
export const saveAirport = async (airportData, idToken, airportId = null) => {
  const result = await saveEntity(
    {
      ...airportData,
      type: "Airport",
    },
    idToken,
    airportId
  );
  return {
    ...result,
    airportId: result.entityId,
  };
};

/**
 * Save non-profit via unified saveEntity mutation.
 */
export const saveNonProfit = async (
  nonprofitData,
  idToken,
  nonprofitId = null,
) => {
  const result = await saveEntity(
    {
      ...nonprofitData,
      type: "NonProfit",
    },
    idToken,
    nonprofitId
  );
  return {
    ...result,
    nonProfitId: result.entityId,
  };
};

/**
 * Save mission via GraphQL API
 *
 * @param {Object} missionData - missionInput shape: title, description, status, departure, arrival, dateTime, etc.
 * @param {string} idToken - Cognito ID token (required)
 * @param {string} missionId - Mission ID for updates (optional)
 * @returns {Promise<Object>} Response with EventId, Message, missionId
 */
export const saveMission = async (missionData, idToken, missionId = null) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;
    const isNonEmpty = (v) =>
      v != null && (typeof v !== "string" || v.trim() !== "");

    const input = {};

    if (isNonEmpty(missionData.title))
      input.title = String(missionData.title).trim();
    if (isNonEmpty(missionData.description))
      input.description = String(missionData.description).trim();
    if (isNonEmpty(missionData.status))
      input.status = String(missionData.status).trim();
    // departure/arrival: missionInput expects locationInput { city, state }
    // Accept flat fields, nested missionData.departure / .arrival objects, or legacy strings.
    const depCity =
      missionData.departureCity ?? missionData.departure?.city ?? "";
    const depState =
      missionData.departureState ?? missionData.departure?.state ?? "";
    const depCityFinal = isNonEmpty(depCity)
      ? String(depCity).trim()
      : typeof missionData.departure === "string" &&
        missionData.departure.trim()
      ? (missionData.departure.split(",")[0] || "").trim()
      : "";
    const depStateFinal = isNonEmpty(depState)
      ? String(depState).trim()
      : typeof missionData.departure === "string" &&
        missionData.departure.includes(",")
      ? (missionData.departure.split(",").pop() || "").trim()
      : "";

    const arrCity = missionData.arrivalCity ?? missionData.arrival?.city ?? "";
    const arrState =
      missionData.arrivalState ?? missionData.arrival?.state ?? "";
    const arrCityFinal = isNonEmpty(arrCity)
      ? String(arrCity).trim()
      : typeof missionData.arrival === "string" && missionData.arrival.trim()
      ? (missionData.arrival.split(",")[0] || "").trim()
      : "";
    const arrStateFinal = isNonEmpty(arrState)
      ? String(arrState).trim()
      : typeof missionData.arrival === "string" &&
        missionData.arrival.includes(",")
      ? (missionData.arrival.split(",").pop() || "").trim()
      : "";

    // missionInput uses flightRoute [{ departure, arrival }] (locationInput), not top-level departure/arrival
    const dep = {};
    if (isNonEmpty(depCityFinal)) dep.city = depCityFinal;
    if (isNonEmpty(depStateFinal)) dep.state = depStateFinal;
    const arr = {};
    if (isNonEmpty(arrCityFinal)) arr.city = arrCityFinal;
    if (isNonEmpty(arrStateFinal)) arr.state = arrStateFinal;
    if (Object.keys(dep).length > 0 || Object.keys(arr).length > 0) {
      const leg = {};
      if (Object.keys(dep).length > 0) leg.departure = dep;
      if (Object.keys(arr).length > 0) leg.arrival = arr;
      input.flightRoute = [leg];
    }
    if (isNonEmpty(missionData.dateTime))
      input.dateTime = String(missionData.dateTime).trim();
    if (isNonEmpty(missionData.missionCategory))
      input.missionCategory = String(missionData.missionCategory).trim();
    if (isNonEmpty(missionData.type))
      input.type = String(missionData.type).trim();
    if (isNonEmpty(missionData.disasterCode))
      input.disasterCode = String(missionData.disasterCode).trim();
    if (isNonEmpty(missionData.pilotStatus))
      input.pilotStatus = String(missionData.pilotStatus).trim();
    if (isNonEmpty(missionData.cargoStatus))
      input.cargoStatus = String(missionData.cargoStatus).trim();
    if (isNonEmpty(missionData.confirmedPilotCertificateNumber))
      input.confirmedPilotCertificateNumber = String(
        missionData.confirmedPilotCertificateNumber,
      ).trim();
    if (isNonEmpty(missionData.missionPOCName))
      input.missionPOCName = String(missionData.missionPOCName).trim();
    if (isNonEmpty(missionData.missionPOCOrgId))
      input.missionPOCOrgId = String(missionData.missionPOCOrgId).trim();
    if (isNonEmpty(missionData.missionPOCPhone))
      input.missionPOCPhone = String(missionData.missionPOCPhone).trim();
    if (isNonEmpty(missionData.postedBy))
      input.postedBy = String(missionData.postedBy).trim();
    // favoritedBy is backend-managed (collection of people who favorited/expressed interest)
    // interestedPilots is backend-managed (collection of pilots applying to fly the mission)
    if (isNonEmpty(missionData.minHours))
      input.minHours = String(missionData.minHours).trim();
    if (isNonEmpty(missionData.minRating))
      input.minRating = String(missionData.minRating).trim();
    if (
      missionData.linkedRequestId != null &&
      missionData.linkedRequestId !== ""
    )
      input.linkedRequestId = parseInt(missionData.linkedRequestId, 10);
    if (missionData.livesImpacted != null && missionData.livesImpacted !== "")
      input.livesImpacted = parseInt(missionData.livesImpacted, 10);
    if (missionData.milesCovered != null && missionData.milesCovered !== "")
      input.milesCovered = parseFloat(missionData.milesCovered);
    if (
      Array.isArray(missionData.contributingOrgs) &&
      missionData.contributingOrgs.length > 0
    ) {
      input.contributingOrgs = missionData.contributingOrgs
        .filter((s) => isNonEmpty(s))
        .map((s) => String(s).trim());
    }
    // cargoItemInput: description, quantity, weightLbs only (shared with resource requests)
    if (
      Array.isArray(missionData.cargoItems) &&
      missionData.cargoItems.length > 0
    ) {
      input.cargoItems = missionData.cargoItems
        .map((item) => {
          const c = {};
          if (isNonEmpty(item.description))
            c.description = String(item.description).trim();
          const qty =
            item.quantity != null && String(item.quantity).trim() !== ""
              ? String(item.quantity).trim()
              : item.count != null && item.count !== ""
              ? String(item.count)
              : null;
          if (qty != null) c.quantity = qty;
          if (item.weightLbs != null && item.weightLbs !== "")
            c.weightLbs = parseInt(String(item.weightLbs), 10);
          return c;
        })
        .filter((c) => Object.keys(c).length > 0);
    }
    if (
      missionData.pilotRequirements &&
      typeof missionData.pilotRequirements === "object"
    ) {
      const pr = missionData.pilotRequirements;
      const prInput = {};
      if (isNonEmpty(pr.commercialRating))
        prInput.commercialRating = String(pr.commercialRating).trim();
      if (isNonEmpty(pr.flightHourMinimums))
        prInput.flightHourMinimums = String(pr.flightHourMinimums).trim();
      if (isNonEmpty(pr.instrumentFlightRating))
        prInput.instrumentFlightRating = String(
          pr.instrumentFlightRating,
        ).trim();
      if (isNonEmpty(pr.privatePilotsLicense))
        prInput.privatePilotsLicense = String(pr.privatePilotsLicense).trim();
      if (Object.keys(prInput).length > 0) input.pilotRequirements = prInput;
    }
    const missionPassengers =
      missionData.passengerItem ||
      missionData.PassengerItem ||
      missionData.passengerItems;
    if (Array.isArray(missionPassengers) && missionPassengers.length > 0) {
      input.passengerItem = missionPassengers
        .map((item) => {
          const p = {};
          if (item.age != null && item.age !== "")
            p.age = parseInt(item.age, 10);
          if (isNonEmpty(item.fullName))
            p.fullName = String(item.fullName).trim();
          if (isNonEmpty(item.specialNeeds))
            p.specialNeeds = String(item.specialNeeds).trim();
          if (item.weightLbs != null && item.weightLbs !== "")
            p.weightLbs = parseInt(item.weightLbs, 10);
          if (item.isAnimal !== undefined && item.isAnimal !== null) {
            p.isAnimal =
              item.isAnimal === true ||
              String(item.isAnimal || "").trim().toLowerCase() === "yes"
                ? "Yes"
                : "No";
          }
          return p;
        })
        .filter((p) => Object.keys(p).length > 0);
    }
    if (missionData.image && typeof missionData.image === "object") {
      const fileName = missionData.image.fileName ?? missionData.image.fileKey;
      if (isNonEmpty(fileName)) {
        input.image = { fileName: String(fileName).trim() };
        const bucket = missionData.image.bucket;
        if (isNonEmpty(bucket)) {
          input.image.bucket = String(bucket).trim();
        }
      }
    }

    if (Array.isArray(missionData.legs) && missionData.legs.length > 0) {
      input.legs = missionData.legs
        .map((leg) => {
          const l = {};
          const start = leg.startAirport ?? leg.departureAirportId ?? "";
          const end = leg.endAirport ?? leg.arrivalAirportId ?? "";
          const pilotId = leg.pilotId ?? leg.pilot?.profileId ?? "";
          const legId = leg.legId ?? leg.LegId ?? "";
          if (isNonEmpty(legId)) l.legId = String(legId).trim();
          if (isNonEmpty(start)) l.startAirport = String(start).trim();
          if (isNonEmpty(end)) l.endAirport = String(end).trim();
          if (isNonEmpty(pilotId)) {
            l.pilot = {
              profileId: String(pilotId).trim(),
            };
            if (isNonEmpty(leg.pilot?.name))
              l.pilot.name = String(leg.pilot.name).trim();
          }
          return l;
        })
        .filter((l) => Object.keys(l).length > 0);
    }

    const variables = { input };
    if (missionId) variables.missionId = String(missionId).trim();

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: SAVE_MISSION_MUTATION,
        variables,
      }),
    });

    const result = await response.json();

    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to save mission");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const saveResult = result.data?.saveMission;
    return {
      success: true,
      data: saveResult,
      eventId: saveResult?.EventId,
      message: saveResult?.Message,
      missionId: saveResult?.missionId,
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("An unexpected error occurred while saving the mission");
  }
};

/**
 * Save resource request via GraphQL API
 *
 * @param {Object} requestData - resourceRequestInput (schema): title, type, description,
 *   additionalNotes, dueDate, requesterName, status, isAsap,
 *   cargoItems [{ description, quantity, weightLbs }],
 *   passengerItem | PassengerItem (normalized to passengerItem, including isAnimal). Optional location* for [Route] in description.
 * @param {string} idToken - Cognito ID token (required)
 * @param {string} requestId - Request ID for updates (optional)
 * @returns {Promise<Object>} Response with EventId, Message, requestId
 */
export const saveResourceRequest = async (
  requestData,
  idToken,
  requestId = null,
) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;
    const isNonEmpty = (v) =>
      v != null && (typeof v !== "string" || v.trim() !== "");

    // resourceRequestInput (schema): title, type, description, additionalNotes,
    // dueDate, requesterName, status,
    // isAsap, cargoItems [description, quantity, weightLbs], passengerItem (camelCase)
    const input = {};

    if (isNonEmpty(requestData.title))
      input.title = String(requestData.title).trim();
    if (isNonEmpty(requestData.type))
      input.type = String(requestData.type).trim();

    let description = isNonEmpty(requestData.description)
      ? String(requestData.description).trim()
      : "";
    // Backend no longer has origin/destination fields; preserve route in description for display/search.
    const origin = [requestData.locationCity, requestData.locationState]
      .filter(Boolean)
      .join(", ");
    const dest = [requestData.arrivalCity, requestData.arrivalState]
      .filter(Boolean)
      .join(", ");
    if (origin || dest) {
      const routeLine = [
        origin && `Origin: ${origin}`,
        dest && `Destination: ${dest}`,
      ]
        .filter(Boolean)
        .join(" | ");
      const stripped = description.replace(/\n\n\[Route\][\s\S]*$/, "").trim();
      description = stripped
        ? `${stripped}\n\n[Route]\n${routeLine}`
        : `[Route]\n${routeLine}`;
    }
    if (isNonEmpty(description)) input.description = description;
    if (isNonEmpty(requestData.additionalNotes)) {
      input.additionalNotes = String(requestData.additionalNotes).trim();
    }
    // questionResponses removed — answers live on passengerItem fields

    if (isNonEmpty(requestData.dueDate))
      input.dueDate = String(requestData.dueDate).trim();
    if (isNonEmpty(requestData.requesterName))
      input.requesterName = String(requestData.requesterName).trim();
    if (isNonEmpty(requestData.status))
      input.status = String(requestData.status).trim();

    if (requestData.isAsap !== undefined && requestData.isAsap !== null) {
      const on =
        requestData.isAsap === true ||
        String(requestData.isAsap).toLowerCase() === "yes" ||
        String(requestData.isAsap).toLowerCase() === "true";
      input.isAsap = on ? "Yes" : "No";
    }

    if (
      Array.isArray(requestData.cargoItems) &&
      requestData.cargoItems.length > 0
    ) {
      input.cargoItems = requestData.cargoItems.map((c) => ({
        description: isNonEmpty(c.description)
          ? String(c.description).trim()
          : null,
        quantity:
          c.quantity != null && String(c.quantity).trim() !== ""
            ? String(c.quantity).trim()
            : null,
        weightLbs:
          c.weightLbs != null && c.weightLbs !== ""
            ? parseInt(String(c.weightLbs), 10)
            : null,
      }));
    }

    const passengerList =
      requestData.passengerItem ||
      requestData.PassengerItem ||
      requestData.passengerItems;
    if (Array.isArray(passengerList) && passengerList.length > 0) {
      const passengerStringFields = [
        "animalBreed",
        "animalDescription",
        "animalSex",
        "animalType",
        "fullName",
        "guardianEmail",
        "guardianFirstName",
        "guardianLastName",
        "multipleReceivingShelters",
        "multipleSendingShelters",
        "receiverAddress",
        "receiverEmail",
        "receiverFirstName",
        "receiverIs501c3",
        "receiverLastName",
        "receiverOrganization",
        "receiverPhone",
        "receiverWebsite",
        "referralSource",
        "senderIs501c3",
        "sendingOrganization",
        "sendingOrganizationAddress",
        "sendingOrganizationWebsite",
        "specialNeeds",
        "studentEmail",
        "studentEthnicity",
        "studentFlownBefore",
        "studentGender",
        "studentGrade",
        "studentLocation",
        "waiverSigned",
      ];
      input.passengerItem = passengerList.map((p) => {
        const row = {
          age: p.age != null && p.age !== "" ? parseInt(String(p.age), 10) : null,
          weightLbs:
            p.weightLbs != null && p.weightLbs !== ""
              ? parseInt(String(p.weightLbs), 10)
              : null,
          isAnimal:
            p.isAnimal === true ||
            String(p.isAnimal || "").trim().toLowerCase() === "yes"
              ? "Yes"
              : "No",
        };
        passengerStringFields.forEach((field) => {
          if (isNonEmpty(p[field])) {
            row[field] = String(p[field]).trim();
          }
        });
        return row;
      });
    }

    if (requestData.image && typeof requestData.image === "object") {
      const fileName = requestData.image.fileName ?? requestData.image.fileKey;
      if (isNonEmpty(fileName)) {
        // imageFile.bucket is required; resource requests use a fixed bucket
        input.image = {
          fileName: String(fileName).trim(),
          bucket: isNonEmpty(requestData.image.bucket)
            ? String(requestData.image.bucket).trim()
            : "info.avipair.com",
        };
      }
    }

    const variables = { input };
    if (requestId) variables.requestId = String(requestId).trim();

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: SAVE_RESOURCE_REQUEST_MUTATION,
        variables,
      }),
    });

    const result = await response.json();

    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to save resource request");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
      data: result.data?.saveResourceRequest,
      eventId: result.data?.saveResourceRequest?.EventId,
      message: result.data?.saveResourceRequest?.Message,
      requestId: result.data?.saveResourceRequest?.requestId,
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error(
      "An unexpected error occurred while saving the resource request",
    );
  }
};

/**
 * Get resource requests list via GraphQL API
 *
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<Object>} Response with resource requests data (AWSJSON)
 */
/**
 * Get tasks list via GraphQL API
 *
 * @param {string} idToken - Cognito ID token (required)
 * @param {string|null} limit - Optional page size
 * @param {string|null} nextToken - Optional pagination token
 * @returns {Promise<{ success: boolean, data: Array, nextToken: string|null, raw: * }>}
 */
export const getTasks = async (idToken, limit = null, nextToken = null) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }

  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;
    const variables = {};
    if (limit) variables.limit = limit;
    if (nextToken) variables.nextToken = nextToken;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query: GET_TASKS_QUERY,
        variables: Object.keys(variables).length > 0 ? variables : undefined,
      }),
    });

    const result = await response.json();

    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to fetch tasks");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = result.data?.getTasks;
    const { list, nextToken: paginationToken } = parseTasksPayload(data);
    let raw = data;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        raw = data;
      }
    }

    return {
      success: true,
      data: list,
      nextToken: paginationToken,
      raw,
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("Failed to fetch tasks");
  }
};

/**
 * Parse AWSJSON list responses that may include a nextToken cursor.
 * Does not use item_count — backend page size is fixed server-side.
 */
function parseCursorListPayload(data, arrayKeys = ["items", "data", "list"]) {
  if (data == null) return { list: [], nextToken: null };

  let parsed = data;
  while (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return { list: [], nextToken: null };
    }
  }

  if (Array.isArray(parsed)) {
    return { list: parsed, nextToken: null };
  }

  if (!parsed || typeof parsed !== "object") {
    return { list: [], nextToken: null };
  }

  let list = [];
  for (const key of arrayKeys) {
    if (Array.isArray(parsed[key])) {
      list = parsed[key];
      break;
    }
  }

  const nextToken =
    parsed.nextToken ?? parsed.NextToken ?? parsed.next_token ?? null;

  return {
    list,
    nextToken:
      nextToken != null && String(nextToken).trim() !== ""
        ? String(nextToken)
        : null,
  };
}

export const getResourceRequests = async (idToken, nextToken = null) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;
    const query = `
      query GetResourceRequests($nextToken: String) {
        getResourceRequests(nextToken: $nextToken)
      }
    `;
    const variables = {};
    if (nextToken != null && String(nextToken).trim() !== "") {
      variables.nextToken = String(nextToken).trim();
    }
    const requestBody = { query };
    if (Object.keys(variables).length > 0) {
      requestBody.variables = variables;
    }
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify(requestBody),
    });
    const result = await response.json();
    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to fetch resource requests");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = result.data?.getResourceRequests;
    const { list, nextToken: paginationToken } = parseCursorListPayload(data, [
      "ResourceRequestsList",
      "ResourceRequests",
      "resourceRequests",
      "requests",
      "items",
      "data",
      "list",
      "results",
      "records",
      "content",
      "rows",
    ]);
    return {
      success: true,
      data: list,
      nextToken: paginationToken,
      raw: data,
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("Failed to fetch resource requests");
  }
};

/**
 * Get a single resource request by ID via GraphQL API
 *
 * @param {string} requestId - Resource request ID (required)
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<Object>} Response with resource request data (AWSJSON)
 */
export const getResourceRequestById = async (requestId, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!requestId) {
    throw new Error("requestId is required");
  }
  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;
    const query = `
      query GetResourceRequestId($requestId: String!) {
        getResourceRequestId(requestId: $requestId)
      }
    `;
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query,
        variables: { requestId: String(requestId).trim() },
      }),
    });
    const result = await response.json();
    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to fetch resource request");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = result.data?.getResourceRequestId;
    const parsed =
      data == null ? null : typeof data === "string" ? JSON.parse(data) : data;
    return { success: true, data: parsed };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("Failed to fetch resource request");
  }
};

/**
 * Get missions list via GraphQL API (cursor pagination via nextToken only).
 *
 * @param {string} idToken - Cognito ID token (required)
 * @param {string|null} nextToken - Cursor from previous response for Next
 * @returns {Promise<{ success: boolean, data: Array, nextToken: string|null, raw: * }>}
 */
export const getMissions = async (idToken, nextToken = null) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;
    const query = `
      query GetMissions($nextToken: String) {
        getMissions(nextToken: $nextToken)
      }
    `;
    const variables = {};
    if (nextToken != null && String(nextToken).trim() !== "") {
      variables.nextToken = String(nextToken).trim();
    }
    const requestBody = { query };
    if (Object.keys(variables).length > 0) {
      requestBody.variables = variables;
    }
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify(requestBody),
    });
    const result = await response.json();
    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to fetch missions");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = result.data?.getMissions;
    const { list, nextToken: paginationToken } = parseCursorListPayload(data, [
      "Missions",
      "MissionsList",
      "MissionList",
      "missions",
      "items",
      "data",
      "list",
    ]);
    return {
      success: true,
      data: list,
      nextToken: paginationToken,
      raw: data,
    };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("Failed to fetch missions");
  }
};

/**
 * Get a single mission by ID via GraphQL API
 *
 * @param {string} missionId - Mission ID (required)
 * @param {string} idToken - Cognito ID token (required)
 * @returns {Promise<Object>} Response with mission data (AWSJSON)
 */
export const getMissionById = async (missionId, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!missionId) {
    throw new Error("missionId is required");
  }
  try {
    const validToken = await getValidToken(idToken);
    const endpoint = awsconfig.API.GraphQL.endpoint;
    const query = `
      query GetMissionbyId($missionId: String!) {
        getMissionbyId(missionId: $missionId)
      }
    `;
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: validToken,
      },
      body: JSON.stringify({
        query,
        variables: { missionId: String(missionId).trim() },
      }),
    });
    const result = await response.json();
    if (result.errors?.length > 0) {
      const msg = result.errors.map((e) => e.message).join(", ");
      throw new Error(msg || "Failed to fetch mission");
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = result.data?.getMissionbyId;
    const parsed =
      data == null ? null : typeof data === "string" ? JSON.parse(data) : data;
    return { success: true, data: parsed };
  } catch (error) {
    if (error.message) throw new Error(error.message);
    throw new Error("Failed to fetch mission");
  }
};

export const createPaymentSession = async (input = {}, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }

  const validToken = await getValidToken(idToken);
  const endpoint = awsconfig.API.GraphQL.endpoint;
  const mutation = `
    mutation CreatePaymentSession($input: CreatePaymentSessionInput) {
      createPaymentSession(input: $input) {
        url
        sessionId
      }
    }
  `;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: validToken,
    },
    body: JSON.stringify({ query: mutation, variables: { input } }),
  });
  const result = await response.json();
  if (result.errors?.length > 0) {
    throw new Error(result.errors.map((e) => e.message).join(", "));
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return { success: true, data: result.data?.createPaymentSession };
};

export const syncPaymentSession = async (sessionId, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!sessionId) {
    throw new Error("sessionId is required");
  }

  const validToken = await getValidToken(idToken);
  const endpoint = awsconfig.API.GraphQL.endpoint;
  const mutation = `
    mutation SyncPaymentSession($sessionId: String!) {
      syncPaymentSession(sessionId: $sessionId) {
        entityId
        entityType
        status
        planCode
        stripeCustomerId
        stripeSubscriptionId
        stripePriceId
        currentPeriodStart
        currentPeriodEnd
        cancelAtPeriodEnd
        updatedAt
      }
    }
  `;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: validToken,
    },
    body: JSON.stringify({ query: mutation, variables: { sessionId } }),
  });
  const result = await response.json();
  if (result.errors?.length > 0) {
    throw new Error(result.errors.map((e) => e.message).join(", "));
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return { success: true, data: result.data?.syncPaymentSession || null };
};

export const getSubscriptionStatus = async (entityId, idToken) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!entityId) {
    throw new Error("entityId is required");
  }

  const validToken = await getValidToken(idToken);
  const endpoint = awsconfig.API.GraphQL.endpoint;
  const query = `
    query GetSubscriptionStatus($entityId: String!) {
      getSubscriptionStatus(entityId: $entityId) {
        entityId
        entityType
        status
        planCode
        stripeCustomerId
        stripeSubscriptionId
        stripePriceId
        currentPeriodStart
        currentPeriodEnd
        cancelAtPeriodEnd
        updatedAt
      }
    }
  `;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: validToken,
    },
    body: JSON.stringify({ query, variables: { entityId } }),
  });
  const result = await response.json();
  if (result.errors?.length > 0) {
    throw new Error(result.errors.map((e) => e.message).join(", "));
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return { success: true, data: result.data?.getSubscriptionStatus || null };
};

export const getInvoiceHistory = async (profileId, idToken, limit = 10, nextToken = null) => {
  if (!idToken) {
    throw new Error("Authentication token is required");
  }
  if (!profileId) {
    throw new Error("profileId is required");
  }

  const validToken = await getValidToken(idToken);
  const endpoint = awsconfig.API.GraphQL.endpoint;
  const query = `
    query GetInvoiceHistory($profileId: String!, $limit: Int, $nextToken: String) {
      getInvoiceHistory(profileId: $profileId, limit: $limit, nextToken: $nextToken) {
        items {
          profileId
          stripeInvoiceId
          stripeCustomerId
          stripeSubscriptionId
          status
          currency
          amountPaid
          amountDue
          hostedInvoiceUrl
          invoicePdf
          periodStart
          periodEnd
          paidAt
          createdAt
        }
        nextToken
      }
    }
  `;
  const variables = { profileId, limit };
  if (nextToken) variables.nextToken = nextToken;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: validToken,
    },
    body: JSON.stringify({ query, variables }),
  });
  const result = await response.json();
  if (result.errors?.length > 0) {
    throw new Error(result.errors.map((e) => e.message).join(", "));
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const payload = result.data?.getInvoiceHistory || {};
  return {
    success: true,
    data: payload.items || [],
    nextToken: payload.nextToken || null,
  };
};

/**
 * Get GraphQL client instance (for future use with subscriptions)
 *
 * @returns {GraphQLClient} Configured GraphQL client from Amplify
 */
export const getGraphQLClient = () => {
  return generateClient();
};

const graphqlService = {
  saveUser,
  adminSaveUser,
  updateUserRole,
  getUsers,
  getUser,
  getUserProfile,
  getActions,
  getTimeline,
  getMissionDocuments,
  getResourceRequestDocuments,
  getPilotDocuments,
  getFile,
  initializeFileUpload,
  getPresignedUploadUrls,
  finalizeFileUpload,
  addMissionAction,
  updateTask,
  addResourceRequestAction,
  getEntities,
  getEntityById,
  saveEntity,
  getNonProfits,
  getTasks,
  getAirports,
  getAirportById,
  getNonProfitById,
  saveAirport,
  saveNonProfit,
  saveMission,
  getMissions,
  getMissionById,
  saveResourceRequest,
  getResourceRequests,
  getResourceRequestById,
  createPaymentSession,
  syncPaymentSession,
  getSubscriptionStatus,
  getInvoiceHistory,
  getGraphQLClient,
};

export default graphqlService;
